// Pure helpers for showing a checklist run's state. The server decides what is
// actually late (at completion time); these drive the badges and sorting.

const minutesBetween = (a, b) => Math.round((b - a) / 60000);

export const allItems = run =>
  ((run && run.sections) || []).flatMap(section => section.items || []);

export const isAnswered = response => Boolean(response && response.status);

export const runProgress = run => {
  const items = allItems(run);
  const responses = (run && run.responses) || {};
  const answered = items.filter(item => isAnswered(responses[item.key])).length;
  return {
    answered,
    total: items.length,
    percent: items.length ? Math.round((answered / items.length) * 100) : 0
  };
};

export const sectionProgress = (section, responses = {}) => {
  const items = section.items || [];
  const answered = items.filter(item => isAnswered(responses[item.key])).length;
  return { answered, total: items.length, done: items.length > 0 && answered === items.length };
};

const timeToMinutes = time => {
  if (!/^\d{2}:\d{2}$/.test(String(time || ''))) return null;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

// When the run's window opens, derived from its absolute due instant so it's
// unaffected by the phone's timezone setting.
export const windowOpensAt = run => {
  if (!run || !run.dueAt) return null;
  const start = timeToMinutes(run.windowStart);
  const due = timeToMinutes(run.dueTime);
  if (start === null || due === null || start > due) return null;
  return new Date(new Date(run.dueAt).getTime() - (due - start) * 60000);
};

const humanMinutes = minutes => {
  const m = Math.abs(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h ${rest}m` : `${h}h`;
};

// A run's state at `now`:
//   key   — done | late | overdue | dueSoon | due | upcoming | anytime
//   label — short text for a badge
//   tone  — success | warning | error | default
export const runState = (run, now = new Date()) => {
  if (!run) return { key: 'anytime', label: '', tone: 'default' };

  if (run.status === 'done') {
    if (run.late && run.dueAt && run.completedAt) {
      const late = minutesBetween(new Date(run.dueAt), new Date(run.completedAt));
      return { key: 'late', label: `Done ${humanMinutes(late)} late`, tone: 'warning' };
    }
    return { key: 'done', label: 'Done', tone: 'success' };
  }

  if (!run.dueAt) return { key: 'anytime', label: 'Any time today', tone: 'default' };

  const due = new Date(run.dueAt);
  if (now > due) {
    return {
      key: 'overdue',
      label: `Overdue ${humanMinutes(minutesBetween(due, now))}`,
      tone: 'error'
    };
  }

  const opens = windowOpensAt(run);
  if (opens && now < opens) {
    return { key: 'upcoming', label: `From ${run.windowStart}`, tone: 'default' };
  }

  const left = minutesBetween(now, due);
  return left <= 15
    ? { key: 'dueSoon', label: `Due in ${humanMinutes(left)}`, tone: 'warning' }
    : { key: 'due', label: `Due ${run.dueTime}`, tone: 'default' };
};

// Overdue first, then what's open by due time, then upcoming, then anything
// without a deadline, then finished work (most recent first).
const RANK = { overdue: 0, dueSoon: 1, due: 2, upcoming: 3, anytime: 4, late: 5, done: 5 };

export const sortRuns = (runs, now = new Date()) =>
  (runs || []).slice().sort((a, b) => {
    const sa = runState(a, now).key;
    const sb = runState(b, now).key;
    if (RANK[sa] !== RANK[sb]) return RANK[sa] - RANK[sb];
    if (RANK[sa] === 5) {
      return new Date(b.completedAt || 0) - new Date(a.completedAt || 0);
    }
    const da = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
    const db = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
    if (da !== db) return da - db;
    return String(a.templateName || '').localeCompare(String(b.templateName || ''));
  });

// Mirror of the server's completion rules, so the app can point at what's left
// before even asking. The server still has the final say.
export const outstandingItems = run => {
  const responses = (run && run.responses) || {};
  const problems = [];
  allItems(run).forEach(item => {
    const r = responses[item.key];
    if (!isAnswered(r)) {
      problems.push({ key: item.key, reason: 'Not answered' });
      return;
    }
    if (r.status === 'no' && !String(r.note || '').trim()) {
      problems.push({
        key: item.key,
        reason: item.type === 'reading' ? 'Add a corrective action' : 'Add a comment'
      });
    }
    if (item.photoRequired && r.status !== 'na' && !r.photo) {
      problems.push({ key: item.key, reason: 'Photo required' });
    }
  });
  return problems;
};

// Items "All good" may fill in: unanswered plain ticks. Photo items need the
// photo, readings need a number and critical items need a person to look, so
// they're always left for a person.
export const markAllCandidates = (section, responses = {}) =>
  ((section && section.items) || [])
    .filter(
      item =>
        (item.type || 'tick') === 'tick' &&
        !item.photoRequired &&
        !item.critical &&
        !isAnswered(responses[item.key])
    )
    .map(item => item.key);

// Whether an item still needs something from the person — used by "Show
// what's left" so it never hides an item that would block completion.
export const itemNeedsAttention = (item, response) => {
  if (!isAnswered(response)) return true;
  if (response.status === 'no' && !String(response.note || '').trim()) return true;
  if (item.photoRequired && response.status !== 'na' && !response.photo) return true;
  return false;
};

// Client-side check of a reading, for instant green/red feedback while typing.
export const readingPasses = (item, value) => {
  const n = Number(value);
  if (value === '' || value === null || value === undefined || !Number.isFinite(n)) return null;
  const has = v => v !== null && v !== undefined && v !== '';
  const min = has(item.min) ? Number(item.min) : null;
  const max = has(item.max) ? Number(item.max) : null;
  if (item.strict) return (min === null || n > min) && (max === null || n < max);
  return (min === null || n >= min) && (max === null || n <= max);
};

const percent = (part, whole) => (whole ? Math.round((part / whole) * 100) : null);

// How a run turned out, as of `now`:
//   onTime | late    — completed
//   partDone         — not completed, but every section in it is answered
//                      (a run scoped to one person whose share is finished)
//   missed           — past its deadline, or from an earlier day, unfinished
//   open             — still has time
export const runOutcome = (run, now = new Date(), today = null) => {
  if (run.status === 'done') return run.late ? 'late' : 'onTime';
  if (run.partDone) return 'partDone';
  const pastDay = Boolean(today && run.date && run.date < today);
  const overdue = run.dueAt && now > new Date(run.dueAt);
  return overdue || (!run.dueAt && pastDay) ? 'missed' : 'open';
};

// History's status filter. "Their part done" counts as done; "outstanding" is
// anything not finished yet, overdue or not.
const OUTCOME_GROUPS = {
  outstanding: ['missed', 'open'],
  done: ['onTime', 'late', 'partDone'],
  late: ['late'],
  missed: ['missed']
};
export const matchesOutcome = (run, filter, now = new Date(), today = null) =>
  !filter || filter === 'all' || (OUTCOME_GROUPS[filter] || []).includes(runOutcome(run, now, today));

// One row per day from `from` to `to` (YYYY-MM-DD) for the History chart.
export const dailySeries = (runs, from, to, now = new Date(), today = null) => {
  const days = [];
  const cursor = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  const pad = n => String(n).padStart(2, '0');
  while (cursor <= end && days.length < 400) {
    days.push(`${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(cursor.getDate())}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  const rows = days.map(date => ({ date, onTime: 0, late: 0, missed: 0, open: 0 }));
  const byDate = new Map(rows.map(r => [r.date, r]));
  (runs || []).forEach(run => {
    const row = byDate.get(run.date);
    if (!row) return;
    const outcome = runOutcome(run, now, today);
    row[outcome === 'partDone' ? 'onTime' : outcome] += 1;
  });
  return rows;
};

// Compliance over a set of runs.
//
// A run only counts toward the completion rate once it's had its chance: it's
// finished, past its deadline, or (with no deadline) from an earlier day.
// Something due this afternoon isn't a failure yet at 9am.
//
// `today` is the site's local date (YYYY-MM-DD), used for runs without a
// deadline.
export const summariseHistory = (runs, now = new Date(), today = null) => {
  const totals = {
    runs: 0,
    eligible: 0,
    done: 0,
    onTime: 0,
    late: 0,
    missed: 0,
    inProgress: 0,
    issues: 0
  };
  const byTemplate = {};
  const issues = [];

  (runs || []).forEach(run => {
    const outcome = runOutcome(run, now, today);
    const isDone = outcome === 'onTime' || outcome === 'late' || outcome === 'partDone';
    const isMissed = outcome === 'missed';

    const t =
      byTemplate[run.templateId] ||
      (byTemplate[run.templateId] = {
        templateId: run.templateId,
        name: run.templateName,
        department: run.department || '',
        runs: 0,
        eligible: 0,
        done: 0,
        onTime: 0,
        late: 0,
        missed: 0,
        issues: 0,
        critical: 0
      });

    totals.runs += 1;
    t.runs += 1;
    if (isDone || isMissed) {
      totals.eligible += 1;
      t.eligible += 1;
    }
    if (isDone) {
      totals.done += 1;
      t.done += 1;
      // A finished share of a run nobody completed has no finish time, so it
      // counts as done but not towards on-time.
      if (outcome === 'late') {
        totals.late += 1;
        t.late += 1;
      } else if (outcome === 'onTime') {
        totals.onTime += 1;
        t.onTime += 1;
      }
    } else if (isMissed) {
      totals.missed += 1;
      t.missed += 1;
    } else {
      totals.inProgress += 1;
    }

    const responses = run.responses || {};
    (run.sections || []).forEach(section => {
      (section.items || []).forEach(item => {
        const r = responses[item.key];
        if (!r || r.status !== 'no') return;
        t.issues += 1;
        if (item.critical) t.critical += 1;
        issues.push({
          key: `${run._id}-${item.key}`,
          runId: run._id,
          date: run.date,
          templateName: run.templateName,
          subtitle: runSubtitle(run),
          sectionTitle: section.title,
          itemLabel: item.label,
          critical: Boolean(item.critical),
          type: item.type,
          value: r.value,
          unit: item.unit || '',
          note: r.note || '',
          byName: r.byName || '',
          at: r.at || null,
          photo: r.photo || null
        });
      });
    });
  });

  totals.issues = issues.length;
  totals.critical = issues.filter(i => i.critical).length;
  totals.completionRate = percent(totals.done, totals.eligible);
  totals.onTimeRate = percent(totals.onTime, totals.onTime + totals.late);

  const templates = Object.values(byTemplate)
    .map(t => ({
      ...t,
      completionRate: percent(t.done, t.eligible),
      onTimeRate: percent(t.onTime, t.onTime + t.late)
    }))
    // Worst first, so problems are at the top; untested ones last.
    .sort((a, b) => {
      const ra = a.completionRate === null ? 101 : a.completionRate;
      const rb = b.completionRate === null ? 101 : b.completionRate;
      return ra - rb || String(a.name).localeCompare(String(b.name));
    });

  // Critical failures first, then newest.
  issues.sort((a, b) =>
    Number(b.critical) - Number(a.critical) ||
    String(b.date).localeCompare(String(a.date)) ||
    String(b.at || '').localeCompare(String(a.at || '')));

  return { totals, templates, issues };
};

// Short label for a run on a card: "Breakfast Buffet" plus its slot or fields.
export const runSubtitle = run => {
  const fields = Object.entries((run && run.fields) || {});
  const labels = ((run && run.runFields) || []).reduce((acc, f) => {
    acc[f.key] = f.label;
    return acc;
  }, {});
  const fieldText = fields.map(([key, value]) => `${labels[key] || key} ${value}`).join(' · ');
  return [run && run.windowLabel, fieldText].filter(Boolean).join(' · ');
};
